// Graph-scope helpers — load/drop named graphs in the Oxigraph WASM store.
//
// Companion to applyShacl/queryShape: those read; these write + unwrite.
// Used by @cogo/kg-repository's Option-A store-scoped validation pattern
// (see docs/agentic/architecture/kg-repository-usage.md §2.4 + docs/agentic/
// client-independence.md §2.2).
//
// Named-graph isolation rationale: loading a per-block JSON-LD payload into
// a dedicated URN graph keeps it out of the default graph where the seed
// ontology lives, so SHACL rules that query the default graph stay
// uninfluenced unless/until they explicitly union named graphs (a v2
// binding-phase concern). Drop is SPARQL 1.1 `DROP GRAPH <iri>` — removes
// the graph and all its triples atomically.

import { getOxiWeb, getStore } from "./core-loader"
// `namedNode` is read from `getOxiWeb()` at call-time rather than imported
// statically. See `core-loader.ts` for the tsx static-linking rationale.

export interface LoadJsonLdResult {
  /** Graph IRI the data was loaded into (echo of input). */
  graphIri: string
  /** Number of triples present in the graph after load. */
  triples: number
  /** Wall-clock load time (ms). */
  durationMs: number
}

/**
 * Load a JSON-LD string into a named graph inside the active KG store.
 *
 * The caller is responsible for {@link dropNamedGraph} afterwards to keep the
 * store clean — leaked graphs waste triples and confuse subsequent loads
 * that reuse the same IRI.
 *
 * Throws:
 *   - If `initKgCore` has not been called (no store).
 *   - If the JSON-LD is syntactically invalid (Oxigraph parser error wrapped
 *     with the graph IRI for context).
 */
export function loadJsonLdIntoNamedGraph(
  jsonLd: string,
  graphIri: string,
): LoadJsonLdResult {
  const store = getStore()
  if (!store) {
    throw new Error(
      "[cogo-kg-browser] loadJsonLdIntoNamedGraph called before initKgCore — store not ready.",
    )
  }
  if (!graphIri || typeof graphIri !== "string") {
    throw new Error(
      "[cogo-kg-browser] loadJsonLdIntoNamedGraph requires a non-empty graphIri string.",
    )
  }
  const t0 = nowMs()
  const graphNode = getOxiWeb().namedNode(graphIri)
  try {
    store.load(jsonLd, {
      format: "application/ld+json",
      to_graph_name: graphNode,
    })
  } catch (e) {
    throw new Error(
      `[cogo-kg-browser] failed to load JSON-LD into <${graphIri}>: ${
        (e as Error).message
      }`,
    )
  }
  const triples = countTriplesInGraph(graphIri)
  return { graphIri, triples, durationMs: nowMs() - t0 }
}

/**
 * Drop a named graph and all its triples via SPARQL 1.1 `DROP GRAPH`.
 *
 * Idempotent: dropping a non-existent graph is a no-op in SPARQL. Returns
 * the number of triples removed (post-drop the graph size is always zero).
 */
export function dropNamedGraph(graphIri: string): { removed: number; durationMs: number } {
  const store = getStore()
  if (!store) {
    throw new Error(
      "[cogo-kg-browser] dropNamedGraph called before initKgCore — store not ready.",
    )
  }
  if (!graphIri || typeof graphIri !== "string") {
    throw new Error(
      "[cogo-kg-browser] dropNamedGraph requires a non-empty graphIri string.",
    )
  }
  const t0 = nowMs()
  const before = countTriplesInGraph(graphIri)
  // DROP SILENT: never throws if graph is absent (SPARQL 1.1 §3.2.2).
  store.update(`DROP SILENT GRAPH <${escapeIri(graphIri)}>`)
  return { removed: before, durationMs: nowMs() - t0 }
}

/**
 * Count triples currently in a named graph. Useful for defensive checks
 * before/after load + drop operations.
 */
export function countTriplesInGraph(graphIri: string): number {
  const store = getStore()
  if (!store) return 0
  const rows = store.query(
    `SELECT (COUNT(*) AS ?c) WHERE { GRAPH <${escapeIri(graphIri)}> { ?s ?p ?o } }`,
  ) as Array<Map<string, { value?: string } | null>>
  if (!rows || rows.length === 0) return 0
  const first = rows[0]
  // Map bindings: key "c" → {value: "N"}
  if (first && typeof (first as any).get === "function") {
    const v = (first as Map<string, { value?: string } | null>).get("c")
    const raw = v?.value ?? "0"
    const n = Number.parseInt(raw, 10)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

function escapeIri(iri: string): string {
  // Reject IRIs with angle brackets to prevent SPARQL injection; legal IRIs
  // never contain <>.
  if (iri.includes("<") || iri.includes(">")) {
    throw new Error(
      `[cogo-kg-browser] refusing graph IRI containing angle brackets: ${iri}`,
    )
  }
  return iri
}

function nowMs(): number {
  const perf = (globalThis as any).performance
  if (perf && typeof perf.now === "function") return perf.now()
  return Date.now()
}
